class BlogFilter extends HTMLElement {
  constructor() {
    super();
    this.cache = new Map();
    this.abortController = null;
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('fading');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1 },
    );
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPopState);
    // Seed the cache so returning to the first-loaded view never refetches.
    this.cache.set(this.normalize(window.location.href), this.innerHTML);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.onPopState);
    this.observer.disconnect();
    if (this.abortController) this.abortController.abort();
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onClick(event) {
    // Let modified clicks (new tab, download, etc.) behave natively.
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    if (event.button !== 0) return;

    const link = event.target.closest('.blog-filters__link, .pagination__item');
    if (!link || !this.contains(link) || !link.href) return;
    if (link.origin !== window.location.origin) return;

    event.preventDefault();

    const isPagination = link.classList.contains('pagination__item');
    this.render(link.href, { scrollTo: isPagination ? 'top' : 'filters' });
  }

  onPopState() {
    // Only react when the blog view itself changed, not a hash or unrelated state.
    this.render(window.location.href, { updateHistory: false, scrollTo: null });
  }

  async render(url, { updateHistory = true, scrollTo = 'filters' } = {}) {
    const key = this.normalize(url);
    if (key === this.normalize(window.location.href) && updateHistory) return;

    if (this.abortController) this.abortController.abort();

    if (updateHistory) window.history.pushState({ blogFilter: true }, '', url);

    if (this.cache.has(key)) {
      this.swap(this.cache.get(key), scrollTo);
      return;
    }

    this.classList.add('is-loading');
    this.setAttribute('aria-busy', 'true');

    const controller = new AbortController();
    this.abortController = controller;

    try {
      const response = await fetch(this.sectionUrl(url), {
        signal: controller.signal,
      });
      if (!response.ok)
        throw new Error(`Blog filter request failed: ${response.status}`);

      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const source = parsed.querySelector('blog-filter');
      if (!source)
        throw new Error('Blog filter: no <blog-filter> in section response');

      this.cache.set(key, source.innerHTML);
      this.swap(source.innerHTML, scrollTo);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      // Fall back to a normal navigation so the user still gets their results.
      window.location.href = url;
    } finally {
      // A superseded request must not clear the loading state of the one that
      // replaced it.
      if (this.abortController === controller) {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
        this.abortController = null;
      }
    }
  }

  swap(html, scrollTo) {
    const scrollLeft = this.filterScroll();

    this.innerHTML = html;

    this.restoreFilterScroll(scrollLeft);
    this.observeFades();
    this.scroll(scrollTo);
  }

  observeFades() {
    // global.js observes .lazy-fade once on load, so swapped-in cards need re-observing
    // or they stay at opacity 0.
    this.querySelectorAll('.lazy-fade').forEach((element) => {
      element.classList.remove('fading');
      this.observer.observe(element);
    });
  }

  filterScroll() {
    const scroller = this.querySelector('[data-blog-filters-scroller]');
    return scroller ? scroller.scrollLeft : 0;
  }

  restoreFilterScroll(scrollLeft) {
    const scroller = this.querySelector('[data-blog-filters-scroller]');
    if (!scroller || !scrollLeft) return;
    scroller.scrollLeft = scrollLeft;
  }

  scroll(target) {
    if (!target) return;

    const anchor =
      target === 'filters'
        ? this.querySelector('[data-blog-filters-scroller]')
        : this;
    if (!anchor) return;

    const header = document.querySelector('#shopify-section-header');
    const offset = header ? header.clientHeight : 0;
    const top =
      anchor.getBoundingClientRect().top + window.pageYOffset - offset;

    // Filter clicks only scroll when the bar has drifted out of view; paging always
    // returns you to the top of the results.
    if (target === 'filters') {
      const rect = anchor.getBoundingClientRect();
      if (rect.top >= offset && rect.bottom <= window.innerHeight) return;
    }

    window.scrollTo({ top, behavior: 'smooth' });
  }

  sectionUrl(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.set('section_id', this.sectionId);
    return parsed.toString();
  }

  normalize(url) {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('section_id');
    parsed.hash = '';
    return parsed.pathname + parsed.search;
  }
}

customElements.define('blog-filter', BlogFilter);
